import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  StakeProgram,
  Transaction,
  TransactionMessage,
  VersionedTransaction
} from '@solana/web3.js'
import { clsx } from 'clsx'
import { Check, CopyIcon } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { StakeAccount } from '../../api/types'

type UnstakeCardProps = {
  account: StakeAccount
  onSuccess: () => void
}

export const UnstakeCard = ({ account, onSuccess }: UnstakeCardProps) => {
  const truncatedAddress = `${account.pubkey.toBase58().slice(0, 8)}...${account.pubkey.toBase58().slice(-8)}`
  const { connection } = useConnection()
  const { publicKey, signTransaction } = useWallet()

  const solAmount = account.lamports / LAMPORTS_PER_SOL

  const [isLoading, setIsLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(account.pubkey.toBase58())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore clipboard errors
    }
  }, [account.pubkey])

  const handleUnstake = useCallback(
    async (stakeAccountPubkey: PublicKey) => {
      if (!publicKey) return

      setIsLoading(true)

      try {
        const transaction = new Transaction()

        // Deactivate stake
        transaction.add(
          StakeProgram.deactivate({
            stakePubkey: stakeAccountPubkey,
            authorizedPubkey: publicKey
          })
        )

        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash()
        transaction.recentBlockhash = blockhash
        transaction.feePayer = publicKey

        // Simulate before sending to catch errors early
        const deactivateMsg = new TransactionMessage({
          payerKey: publicKey,
          recentBlockhash: blockhash,
          instructions: transaction.instructions
        }).compileToLegacyMessage()
        const deactivateVersioned = new VersionedTransaction(deactivateMsg)
        const simulation = await connection.simulateTransaction(deactivateVersioned, { sigVerify: false })
        if (simulation.value.err) {
          throw new Error(`Transaction simulation failed: ${JSON.stringify(simulation.value.err)}`)
        }

        if (!signTransaction) throw new Error('Wallet does not support signTransaction')
        const signedTx = await signTransaction(transaction)
        const signature = await connection.sendRawTransaction(signedTx.serialize())
        await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, 'confirmed')

        toast.success('Stake deactivation initiated! Funds will be available after cooldown period.')
        onSuccess()
      } catch (error) {
        console.error('Unstaking failed:', error)
        toast.error(error instanceof Error ? error.message : 'Unstaking failed')
      } finally {
        setIsLoading(false)
      }
    },
    [publicKey, connection, signTransaction, onSuccess]
  )

  const handleWithdraw = useCallback(
    async (stakeAccountPubkey: PublicKey, lamports: number) => {
      if (!publicKey) return

      setIsLoading(true)

      try {
        const transaction = new Transaction()

        transaction.add(
          StakeProgram.withdraw({
            stakePubkey: stakeAccountPubkey,
            authorizedPubkey: publicKey,
            toPubkey: publicKey,
            lamports
          })
        )

        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash()
        transaction.recentBlockhash = blockhash
        transaction.feePayer = publicKey

        // Simulate before sending to catch errors early
        const withdrawMsg = new TransactionMessage({
          payerKey: publicKey,
          recentBlockhash: blockhash,
          instructions: transaction.instructions
        }).compileToLegacyMessage()
        const withdrawVersioned = new VersionedTransaction(withdrawMsg)
        const withdrawSim = await connection.simulateTransaction(withdrawVersioned, { sigVerify: false })
        if (withdrawSim.value.err) {
          throw new Error(`Transaction simulation failed: ${JSON.stringify(withdrawSim.value.err)}`)
        }

        if (!signTransaction) throw new Error('Wallet does not support signTransaction')
        const signedTx = await signTransaction(transaction)
        const signature = await connection.sendRawTransaction(signedTx.serialize())
        await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, 'confirmed')

        toast.success(`Withdrawn ${(lamports / LAMPORTS_PER_SOL).toFixed(4)} SOL successfully!`)
        onSuccess()
      } catch (error) {
        console.error('Withdrawal failed:', error)
        toast.error(error instanceof Error ? error.message : 'Withdrawal failed')
      } finally {
        setIsLoading(false)
      }
    },
    [publicKey, connection, signTransaction, onSuccess]
  )
  return (
    <div className="rounded-lg border border-gray-100 p-4">
      <div className="mb-3 flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-body-3 text-gray-90">Staking Account</p>
          <button
            type="button"
            onClick={handleCopy}
            className="group text-body-3 text-gray-70 hover:text-orange flex items-center gap-2 transition-colors"
            title={copied ? 'Copied!' : 'Copy address'}
            aria-label={copied ? 'Copied' : 'Copy address'}
          >
            {truncatedAddress}
            {copied ? (
              <Check className="h-3 w-3 shrink-0 opacity-70" />
            ) : (
              <CopyIcon className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-70" aria-hidden />
            )}
          </button>
        </div>
        <span
          className={clsx(
            'text-body-3',
            account.status === 'active' && 'text-green',
            account.status === 'inactive' && 'text-gray-70',
            account.status === 'activating' && 'text-blue-300',
            account.status === 'deactivating' && 'text-yellow'
          )}
        >
          {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex h-[28px] items-center gap-0.5">
          <p className="text-subtitle text-gray-70 pr-3 font-semibold">
            {solAmount > 0 && solAmount < 0.01 ? '<0.01' : Number(solAmount.toFixed(2))} SOL
          </p>
        </div>
        <div className="flex gap-2">
          {(account.status === 'active' || account.status === 'activating') && (
            <button
              type="button"
              onClick={() => handleUnstake(account.pubkey)}
              disabled={isLoading}
              className={clsx(
                'rounded-lg border-2 border-gray-100 px-4 py-1.5 font-semibold',
                !isLoading && 'hover:border-orange'
              )}
            >
              {isLoading ? 'Processing...' : 'Unstake'}
            </button>
          )}
          {(account.status === 'inactive' ||
            (account.status === 'deactivating' && account.activatedLamports === 0) ||
            (account.status === 'deactivating' &&
              account.activationEpoch != null &&
              account.activationEpoch === account.deactivationEpoch)) && (
            <button
              type="button"
              onClick={() => handleWithdraw(account.pubkey, account.lamports)}
              disabled={isLoading}
              className={clsx(
                'rounded-lg border-2 border-gray-100 px-4 py-1.5 font-semibold transition-colors',
                !isLoading && 'border-orange hover:bg-orange'
              )}
            >
              {isLoading ? 'Processing...' : 'Withdraw'}
            </button>
          )}
          {account.status === 'deactivating' &&
            account.activatedLamports > 0 &&
            account.activationEpoch !== account.deactivationEpoch && (
              <div className="flex-1 text-end">
                <p className="text-sm font-medium">Cooldown in progress</p>
                <p className="text-gray-80 text-xs">Withdraw available when fully deactivated</p>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
